import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { isEmpty, startCase } from "lodash";
import { useParams } from "react-router-dom";

import MyAccountLayout from "./MyAccountLayout";
import MyAccountFilter from "./MyAccountFilter";
import CustomDatePicker from "../../components/CustomDatePicker";
import Pagination from "../../components/Pagination";
import { apiGet } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";
import { formatDate, toFixedWithoutRounding } from "../../Utils/Constant";
import AuthContext from "../../context/AuthContext";
import { useContextSelector } from "use-context-selector";

const style = { padding: "8px" };
const styleBody = { padding: "8px 7px" };

const fromToLabel = (item) => {
  if (item?.forBet != 0) {
    const sport =
      item?.eventType === "4"
        ? "Cricket"
        : item?.eventType === "1"
          ? "Soccer"
          : item?.eventType === "2"
            ? "Tennis"
            : "Casino";
    return `${sport} / Match / ${item?.matchName} / ${item?.eventId} / ${item?.runnerName}`;
  }
  return item?.formToData || "-";
};

const amountCell = (value, className) =>
  value ? (
    <span className={className}>{toFixedWithoutRounding(Math.abs(value))}</span>
  ) : (
    "-"
  );

// -- Ported from TransactionHistory2.jsx, used only by the !isEmpty(id)
// (per-user) view below --

const betEventSport = (item) =>
  item?.eventType === "4"
    ? "Cricket"
    : item?.eventType === "1"
      ? "Soccer"
      : item?.eventType === "2"
        ? "Tennis"
        : "Casino";

const betOddsSuffix = (item) =>
  item?.gameType === "cricket" &&
  item?.betFaireType === "betfair" &&
  item?.eventId != ""
    ? "/ Match Odds"
    : item?.gameType === "cricket" &&
        item?.betFaireType === "boomaker" &&
        item?.eventId != ""
      ? "/Bookmaker"
      : "";

const betLogRemark = (item) => {
  const isBet = item?.forBet != 0 || item?.forBet != "0";

  if (item?.status !== "voided" && !item?.isCallback) {
    if (!isBet)
      return item?.transactionType === "credit"
        ? "Deposit by Upline"
        : item?.transactionType === "debit"
          ? "Withdraw from Upline"
          : item?.remark;
    const market =
      item?.betFaireType === "toss"
        ? "/ Toss /"
        : item?.betFaireType === "tie"
          ? "/ Tie /"
          : "/ Match /";
    return `${betEventSport(item)} ${market} ${item?.matchName} / ${item?.eventId || item?.selectionId} / ${item?.runnerName} ${betOddsSuffix(item)}`;
  }
  const text = !isBet
    ? item?.transactionType === "credit"
      ? "Deposit by Upline"
      : item?.transactionType === "debit"
        ? "Withdraw from Upline"
        : item?.remark
    : `${betEventSport(item)} / Match / ${item?.matchName} / ${item?.eventId} / ${item?.runnerName} ${betOddsSuffix(item)}`;

  return <div style={{ color: "red", fontWeight: 400 }}>{text}</div>;
};

const betLogFromTo = (item) => {
  const isBet = item?.forBet != 0 || item?.forBet != "0";
  return (
    <>
      {item?.agentData?.username} <i className="fas fa-arrow-right" />{" "}
      {isBet ? "-->" : `Agent ->${item?.userData?.username}`}
    </>
  );
};

const AccountStatement = () => {
  const { id, type } = useParams();
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);
  const tableRef = useRef();

  const DEFAULT_FILTER = {
    page: 1,
    limit: 10,
    user_id: id,
    userType: type,
    fromPeriod: moment().subtract(1, "day").format("YYYY-MM-DD HH:mm:ss"),
    toPeriod: moment().format("YYYY-MM-DD HH:mm:ss"),
  };
  const [rowData, setRowData] = useState([]);
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [data, setData] = useState({});

  const getData = async (activeFilter) => {
    // Per-user statement now hits the same endpoint TransactionHistory2.jsx
    // used; the aggregate (own) statement keeps the existing one.
    const apiToCall = !isEmpty(id)
      ? apiPath.transactionBetLogs
      : apiPath.transactionLogs;

    const { status, data: response_users } = await apiGet(
      apiToCall,
      activeFilter,
    );
    if (status === 200 && response_users.success) {
      setData(response_users.results);
    }
  };

  useEffect(() => {
    getData(filter);
  }, [filter.page, filter?.limit]);

  const applyPreset = (preset) => {
    const fromPeriod =
      preset === "today"
        ? moment().startOf("day")
        : moment().subtract(1, "day").startOf("day");
    const toPeriod =
      preset === "today"
        ? moment().endOf("day")
        : moment().subtract(1, "day").endOf("day");
    const nextFilter = {
      ...filter,
      page: 1,
      fromPeriod: fromPeriod.format("YYYY-MM-DD HH:mm:ss"),
      toPeriod: toPeriod.format("YYYY-MM-DD HH:mm:ss"),
    };
    setFilter(nextFilter);
    if (filter.page === nextFilter.page) getData(nextFilter);
  };

  const handlePageChange = (newPage) =>
    setFilter((prev) => ({ ...prev, page: newPage }));

  const rows = data?.data || [];

  const totals = rows.reduce(
    (acc, item) => ({
      depositFromUpline:
        acc.depositFromUpline + Number(item?.depositFromUpline || 0),
      depositToDownline:
        acc.depositToDownline + Number(item?.depositToDownline || 0),
      wihtDrawByUpline:
        acc.wihtDrawByUpline + Number(item?.wihtDrawByUpline || 0),
      withDrawFromDownline:
        acc.withDrawFromDownline + Number(item?.withDrawFromDownline || 0),
    }),
    {
      depositFromUpline: 0,
      depositToDownline: 0,
      wihtDrawByUpline: 0,
      withDrawFromDownline: 0,
    },
  );

  const getRowBets = async (item) => {
    const object = {
      betFaireType: item?.betFaireType,
      eventId: item?.eventId,
      marketId: item?.marketId,
      userId: id,
    };

    const { status, data: response } = await apiGet(
      apiPath.userBetsnew,
      object,
    );
    if (status === 200 && response.success) {
      setRowData(response.results);
    }
  };

  const isBetWin = (v) => {
    if (v.exchangeType === "session") {
      return v.type === "No"
        ? v.decisionRun < v.betRun
        : v.decisionRun >= v.betRun;
    }
    const winKey =
      v.exchangeType === "sportbook" ? "fancySelectionId" : "selectionId";
    const selWon = v.teamSelectionWin === v[winKey];
    return v.betType === "lay" ? !selWon : selWon;
  };
  return (
    <MyAccountLayout>
      {!isEmpty(id) && (
        <div id="report" className="col-center report">
          <div className="box" style={{ alignItems: "baseline" }}>
            <h2>Account Statement</h2>
          </div>

          <div id="userTreeDiv" className="total_all">
            <span className="spacer" />
            <MyAccountFilter
              type={"new"}
              tableRef={tableRef}
              fileName="account-statement"
            />
          </div>

          <div className="function-wrap">
            <ul className="input-list">
              <li>
                <label>Period</label>
              </li>
              <CustomDatePicker filter={filter} setFilter={setFilter} />
            </ul>
            <ul className="input-list">
              <li>
                <a
                  id="today"
                  className="btn"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    applyPreset("today");
                  }}
                >
                  Just for today
                </a>
              </li>
              <li>
                <a
                  id="yesterday"
                  className="btn"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    applyPreset("yesterday");
                  }}
                >
                  From yesterday
                </a>
              </li>
              <li>
                <a
                  id="getPL"
                  className="btn-send"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    getData(filter);
                  }}
                >
                  Load Statement
                </a>
              </li>
            </ul>
          </div>
          {rowData?.length > 0 ? (
            <div className="data-table">
              <button onClick={() => setRowData([])} class="add_member btn">
                Back
              </button>
              <table
                _ngcontent-serverapp-c81=""
                id="table_marketbets"
                className="table01 table-responsive"
              >
                <tbody _ngcontent-serverapp-c81="">
                  <tr _ngcontent-serverapp-c81="">
                    <th
                      _ngcontent-serverapp-c81=""
                      width="10%"
                      data-order="asc"
                      data-name="betTime"
                      className="align-L sort_asc"
                    >
                      Placed on
                    </th>
                    <th
                      _ngcontent-serverapp-c81=""
                      width="10%"
                      data-order="asc"
                      data-name="settlementDate"
                      className="align-L sort_asc"
                    >
                      Settled On
                    </th>
                    <th
                      _ngcontent-serverapp-c81=""
                      width="10%"
                      data-order="asc"
                      data-name="betId"
                      className="sort_asc"
                    >
                      Bet ID
                    </th>
                    <th
                      _ngcontent-serverapp-c81=""
                      width="10%"
                      data-order="asc"
                      data-name="betType"
                      className="sort_asc"
                    >
                      betType
                    </th>
                    <th
                      _ngcontent-serverapp-c81=""
                      width="10%"
                      data-order="asc"
                      data-name="odd"
                      className="sort_asc"
                    >
                      Odds
                    </th>
                    <th
                      _ngcontent-serverapp-c81=""
                      width="10%"
                      data-order="asc"
                      data-name="stake"
                      className="sort_asc"
                    >
                      Stake
                    </th>
                    <th
                      _ngcontent-serverapp-c81=""
                      width="15%"
                      data-order="asc"
                      data-name="selectionName"
                      className="sort_asc"
                    >
                      selectionName
                    </th>
                    <th
                      _ngcontent-serverapp-c81=""
                      width="10%"
                      data-order="asc"
                      data-name="status"
                      className="sort_asc"
                    >
                      Won / Lose
                    </th>
                  </tr>
                </tbody>
                <tbody _ngcontent-serverapp-c81="" id="content">
                  {rowData?.map((res) => {
                    const isWin = isBetWin(res);
                    console.log(isWin, "isWin");
                    return (
                      <tr
                        _ngcontent-serverapp-c81=""
                        className={
                          res?.exchangeType === "session"
                            ? res?.type === "No"
                              ? "lay"
                              : "back"
                            : res?.betType === "lay"
                              ? "lay"
                              : "back"
                        }
                      >
                        <td _ngcontent-serverapp-c81="" className="align-L">
                          {formatDate(res?.createdAt)}
                        </td>

                        <td _ngcontent-serverapp-c81="" className="align-L">
                          {formatDate(res?.updatedAt)}
                        </td>

                        <td _ngcontent-serverapp-c81="">
                          {res?.sessionBetId || res?.matchBetId}{" "}
                        </td>
                        <td _ngcontent-serverapp-c81="">
                          <span _ngcontent-serverapp-c81="">
                            {" "}
                            {startCase(res?.type) || startCase(res?.betType)}
                          </span>
                        </td>
                        <td _ngcontent-serverapp-c81="">
                          {res?.exchangeType === "session"
                            ? `${res?.betRun}:${res?.bhav}`
                            : res?.bhav}
                        </td>
                        <td _ngcontent-serverapp-c81="">{res?.amount}</td>
                        <td _ngcontent-serverapp-c81="">
                          {" "}
                          {res?.exchangeType === "session"
                            ? `${res?.fancyName}`
                            : res?.runnerName || res?.teamName}
                        </td>
                        <td
                          _ngcontent-serverapp-c81=""
                          className={isWin ? "green" : "red"}
                        >
                          {isWin ? "Won" : "Loss"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="data-table">
              <table
                ref={tableRef}
                id="table_DL"
                className="table01 table-responsive"
              >
                <tbody>
                  <tr>
                    <th width="10%" className="align-L sort_asc">
                      Date/Time
                    </th>
                    <th width="10%" className="sort_asc">
                      Deposit
                    </th>

                    <th width="10%" className="sort_asc">
                      Withdraw
                    </th>

                    <th width="10%" className="sort_asc">
                      Balance
                    </th>
                    <th width="15%" className="sort_asc">
                      Remark
                    </th>
                  </tr>
                </tbody>
                <tbody id="content">
                  {rows.length > 0 ? (
                    rows.map((item, key) => (
                      <tr key={key}>
                        <td id="createDate" className="align-L">
                          {formatDate(item?.createdAt, user?.timeZone)}
                        </td>
                        <td className="green">
                          {item?.transactionType === "credit" ? (
                            <span className="text-success">
                              {toFixedWithoutRounding(item?.amount)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          {item?.transactionType === "debit" ? (
                            <span className="text-danger">
                            {toFixedWithoutRounding(item?.amount)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td id="balance">
                          {toFixedWithoutRounding(item?.newBalance)}
                        </td>
                        <td id="remark">
                          <a
                            onClick={() => getRowBets(item)}
                            style={{
                              color: isEmpty(item?.gameName)
                                ? ""
                                : item?.gameType != "casino"
                                  ? `#2789ce`
                                  : "",
                            }}
                          >
                            {betLogRemark(item)}
                          </a>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8}>No records found</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {rows.length > 0 && data?.totalDocs > 0 && (
                <Pagination pagination={data} onPageChange={handlePageChange} />
              )}
            </div>
          )}
        </div>
      )}

      {isEmpty(id) && (
        <div id="report" className="col-center report">
          <div className="total_all box">
            <h2>Account Statement</h2>
            <div className="push">
              <MyAccountFilter
                tableRef={tableRef}
                fileName={"account-statement"}
                limit={filter?.limit}
                onChange={(e) => {
                  setFilter((prev) => {
                    return { ...prev, limit: Number(e.target.value), page: 1 };
                  });
                }}
              />
            </div>
          </div>

          <div className="function-wrap">
            <ul className="input-list">
              <li>
                <label>Period</label>
              </li>
              <CustomDatePicker filter={filter} setFilter={setFilter} />
            </ul>
            <ul className="input-list">
              <li>
                <a
                  id="today"
                  className="btn"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    applyPreset("today");
                  }}
                >
                  Just for today
                </a>
              </li>
              <li>
                <a
                  id="yesterday"
                  className="btn"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    applyPreset("yesterday");
                  }}
                >
                  From yesterday
                </a>
              </li>
              <li>
                <a
                  id="getPL"
                  className="btn-send"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    getData(filter);
                  }}
                >
                  Load Statement
                </a>
              </li>
            </ul>
          </div>

          <div className="table-responsive data-table">
            <table id="table_log" ref={tableRef} className="table01">
              <tbody>
                <tr>
                  <th style={style} width="12%" className="align-L sort_asc">
                    Date/Time
                  </th>
                  <th style={style} width="13%" className="sort_asc">
                    Deposit by Upline
                  </th>
                  <th style={style} width="13%" className="sort_asc">
                    Deposit to Downline
                  </th>
                  <th style={style} width="13%" className="sort_asc">
                    Withdraw by Upline
                  </th>
                  <th style={style} width="13%" className="sort_asc">
                    Withdraw from Downline
                  </th>
                  <th style={style} width="13%" className="sort_asc">
                    Balance
                  </th>
                  <th style={style} width="13%" className="sort_asc">
                    Remark
                  </th>
                  <th style={style} className="sort_asc">
                    From/To
                  </th>
                </tr>
              </tbody>
              <tbody id="content">
                {rows.length > 0 ? (
                  rows.map((item, key) => (
                    <tr key={key}>
                      <td style={styleBody} id="createDate" className="align-L">
                        {formatDate(item?.createdAt, user?.timeZone)}
                      </td>
                      <td style={styleBody}>
                        {amountCell(item?.depositFromUpline, "green")}
                      </td>
                      <td style={styleBody}>
                        {amountCell(item?.depositToDownline, "green")}
                      </td>
                      <td style={styleBody}>
                        {amountCell(item?.wihtDrawByUpline, "red")}
                      </td>
                      <td style={styleBody}>
                        {amountCell(item?.withDrawFromDownline, "red")}
                      </td>
                      <td style={styleBody} id="balance">
                        {toFixedWithoutRounding(item?.newBalance)}
                      </td>
                      <td style={styleBody} id="remark">
                        {/* {item?.remark} */}
                        Fund Transfer
                      </td>
                      <td style={styleBody}>{fromToLabel(item)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td style={styleBody} colSpan={8}>
                      No records found
                    </td>
                  </tr>
                )}

                {rows.length > 0 && (
                  <tr className="total">
                    <td style={styleBody} className="align-L">
                      Total
                    </td>
                    <td style={styleBody} id="_totalDepositbyUpline">
                      <span>
                        {toFixedWithoutRounding(totals.depositFromUpline)}
                      </span>
                    </td>
                    <td style={styleBody} id="_totalDepositbyDownline">
                      <span>
                        {toFixedWithoutRounding(totals.depositToDownline)}
                      </span>
                    </td>
                    <td style={styleBody} id="_totalWithdrawUpline">
                      <span>
                        {toFixedWithoutRounding(totals.wihtDrawByUpline)}
                      </span>
                    </td>
                    <td style={styleBody} id="_totalWithdrawDownline">
                      <span>
                        {toFixedWithoutRounding(totals.withDrawFromDownline)}
                      </span>
                    </td>
                    <td style={styleBody} />
                    <td style={styleBody} />
                    <td style={styleBody} />
                  </tr>
                )}
              </tbody>
            </table>

            {rows.length > 0 && data?.totalDocs > 0 && (
              <Pagination pagination={data} onPageChange={handlePageChange} />
            )}
          </div>
        </div>
      )}
    </MyAccountLayout>
  );
};

export default AccountStatement;
